"use client";

import { motion } from "framer-motion";
import CountUp from "react-countup";

import CustomImage from "@/components/shared/CustomImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFancybox } from "@/hooks/useFancybox";
import type { About, Media } from "@/payload-types";
import getImageUrl from "@/utils/getImageUrl";
import type { PayLoadData } from "@/utils/payloadUtil";

export default function HistoryAbout({
  pageData,
}: {
  pageData: PayLoadData<About | undefined> | undefined;
}) {
  const { language } = useLanguage();

  useFancybox();

  return (
    <section id="history-about" className="bg-white">
      <div className="container mx-auto px-4">
        {/* Founding History Section - NEW */}
        <div className="flex flex-col items-center gap-8 md:gap-12 lg:flex-row lg:gap-10 xl:gap-20">
          {/* Box content */}
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="w-full lg:w-2/5"
          >
            <div>
              <h2 className="mb-5 text-center text-3xl font-bold md:text-4xl lg:text-left">
                {pageData?.[language]?.historyOfStory.title || ""}
              </h2>
              <p className="text-justify text-lg text-gray-600">
                {pageData?.[language]?.historyOfStory.desc || ""}
              </p>
            </div>
          </motion.div>

          {/* Box info (img & counterUp) */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="w-full lg:w-3/5 lg:space-y-8"
          >
            <div className="space-y-4">
              {/* row 1 */}
              {pageData?.[language]?.historyOfStory.album?.map((item, idx) => (
                <div
                  key={idx}
                  className={`flex gap-4 lg:gap-8 ${idx % 2 === 0 ? "items-end" : "items-start"}`}
                >
                  <div
                    className={`relative h-56 w-1/2 flex-1 overflow-hidden ${idx % 2 === 0 ? "" : "order-2"}`}
                  >
                    <a
                      href={getImageUrl(item.image[0])}
                      data-fancybox="gallery-history-1"
                      className="block size-full"
                    >
                      <CustomImage
                        src={getImageUrl(item.image[0])}
                        alt={(item.image[0] as Media).alt || "image"}
                        fill
                        className="size-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                    </a>

                    {/* Fancybox gallery */}
                    <div style={{ display: "none" }}>
                      {item?.image?.map((img, imgIdx) => (
                        <a
                          key={imgIdx}
                          data-fancybox={`gallery-history-${idx}`}
                          href={getImageUrl(img)}
                        >
                          <CustomImage
                            src={getImageUrl(img)}
                            alt={(img as Media).alt || "image"}
                            fill
                            className="size-full"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                  <div className="flex h-56 w-1/2 flex-1 flex-col items-center justify-center bg-primary-500 py-10 text-center text-white lg:h-auto">
                    <div className="text-3xl font-semibold md:text-5xl lg:text-6xl">
                      <CountUp
                        start={0}
                        end={item?.numberCounter ?? 0}
                        duration={5}
                        separator=","
                        suffix="+"
                      />
                    </div>
                    <p className="mt-1 text-lg">{item?.titleCounter}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
